import {
    useSpecificationsByIdGetV2,
    useSpecificationsExternalSpecificationsByShareTokenAndExternalIdGetV2,
    useSpecificationsExternalSpecificationsByShareTokenGetV2,
    useSpecificationsLookupListGetV2,
} from "@btnet/services-btnet/handlers/specifications/specifications";

/**
 * Hook to select between internal and external specifications list endpoint
 * based on the presence of a shareToken.
 *
 * Internal mode: Requires jobId, returns specifications for authenticated user
 * External mode: Requires shareToken, returns published specifications for anonymous user
 *
 * @param shareToken - Optional share token for external/anonymous access
 * @param jobId - Optional job ID for internal/authenticated access
 * @returns React Query result from the appropriate API endpoint
 */
export const useSpecificationsListEndpointSelector = (
    shareToken: string | undefined,
    jobId?: number,
) => {
    // Internal endpoint - requires jobId for authenticated users
    const internalApi = useSpecificationsLookupListGetV2(
        { jobId: jobId ?? 0 },
        { query: { enabled: !shareToken && !!jobId } },
    );

    // External endpoint - requires shareToken for anonymous users
    const externalApi = useSpecificationsExternalSpecificationsByShareTokenGetV2(shareToken ?? "", {
        query: { enabled: !!shareToken },
    });

    // Return the appropriate API based on mode
    return shareToken ? externalApi : internalApi;
};

/**
 * Hook to select between internal and external specification detail endpoint
 * based on the presence of a shareToken.
 *
 * Internal mode: Requires specificationId + jobId, returns full spec details for authenticated user
 * External mode: Requires shareToken + externalId, returns published spec details for anonymous user
 *
 * @param shareToken - Optional share token for external/anonymous access
 * @param externalId - Optional external GUID for external/anonymous access
 * @param specificationId - Optional specification ID for internal/authenticated access
 * @param jobId - Optional job ID for internal/authenticated access
 * @returns React Query result from the appropriate API endpoint
 */
export const useSpecificationByIdEndpointSelector = (
    shareToken: string | undefined,
    externalId: string | undefined,
    specificationId: number | null,
    jobId?: number,
    bidId?: number,
) => {
    // Internal endpoint - requires specificationId and jobId for authenticated users
    const internalApi = useSpecificationsByIdGetV2(
        specificationId ?? 0,
        { jobId: jobId ?? 0, bidId },
        { query: { enabled: !shareToken && !!specificationId && !!jobId } },
    );

    // External endpoint - requires shareToken and externalId for anonymous users
    const externalApi = useSpecificationsExternalSpecificationsByShareTokenAndExternalIdGetV2(
        shareToken ?? "",
        externalId ?? "",
        { query: { enabled: !!shareToken && !!externalId } },
    );

    // Return the appropriate API based on mode
    return shareToken ? externalApi : internalApi;
};
